import React, { useState, useRef } from 'react';
import { ServiceRequest, ServiceRequestResponse } from '@/types/entities/vendor/serviceRequest';
import { useVendorServiceResponse, useVendorFileUpload } from '@/hooks/entities/vendor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { categoryLabels, priorityLabels } from '@/types/entities/vendor/serviceRequest';
import { Plus, X, Upload, FileImage, Loader2 } from 'lucide-react';

interface ServiceRequestResponseModalProps {
  serviceRequest: ServiceRequest;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (response: Partial<ServiceRequestResponse>) => void;
}

interface PortfolioItem {
  title: string;
  description: string;
  imageUrl?: string;
  imageFile?: File;
}

export function ServiceRequestResponseModal({
  serviceRequest,
  isOpen,
  onClose,
  onSubmit,
}: ServiceRequestResponseModalProps) {
  const { submitResponse, isSubmitting, error } = useVendorServiceResponse();
  const { uploadFile, isUploading } = useVendorFileUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentPortfolioIndex, setCurrentPortfolioIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    message: '',
    estimatedCost: '',
    estimatedDuration: '',
    availability: '',
    certifications: [] as string[],
  });

  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [newCertification, setNewCertification] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const portfolioData = portfolioItems.map((item) => ({
        title: item.title,
        description: item.description,
        imageFile: item.imageFile,
        imageUrl: item.imageUrl,
      }));

      const responseData = {
        serviceRequestId: serviceRequest.id,
        message: formData.message,
        estimatedCost: formData.estimatedCost ? parseFloat(formData.estimatedCost) : undefined,
        estimatedDuration: formData.estimatedDuration || undefined,
        availability: formData.availability || undefined,
        certifications: formData.certifications.filter((cert) => cert.trim() !== ''),
        portfolioItems: portfolioData,
      };

      const response = await submitResponse(responseData);
      await onSubmit(response);
      onClose();
    } catch (error) {
      console.error('Failed to submit response:', error);
    }
  };

  const addCertification = () => {
    if (newCertification.trim()) {
      setFormData((prev) => ({
        ...prev,
        certifications: [...prev.certifications, newCertification.trim()],
      }));
      setNewCertification('');
    }
  };

  const removeCertification = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index),
    }));
  };

  const updatePortfolioItem = (
    index: number,
    field: keyof PortfolioItem,
    value: PortfolioItem[keyof PortfolioItem],
  ) => {
    setPortfolioItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const addPortfolioItem = () => {
    setPortfolioItems((prev) => [...prev, { title: '', description: '' }]);
  };

  const removePortfolioItem = (index: number) => {
    setPortfolioItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (index: number) => {
    setCurrentPortfolioIndex(index);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || currentPortfolioIndex === null) return;

    try {
      // Store the file directly in the portfolio item
      updatePortfolioItem(currentPortfolioIndex, 'imageFile', file);
      updatePortfolioItem(currentPortfolioIndex, 'imageUrl', ''); // Clear URL if file is uploaded

      // Optionally upload immediately and get URL
      // const uploadResult = await uploadFile(file);
      // updatePortfolioItem(currentPortfolioIndex, 'imageUrl', uploadResult.url);
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setCurrentPortfolioIndex(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Respond to Service Request</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Service Request Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <h4 className="font-medium">{serviceRequest.title}</h4>
                <p className="text-sm text-muted-foreground mt-1">{serviceRequest.description}</p>
              </div>

              <div className="flex gap-2">
                <Badge variant="outline">{categoryLabels[serviceRequest.category]}</Badge>
                <Badge variant="secondary">{priorityLabels[serviceRequest.priority]}</Badge>
              </div>

              {serviceRequest.budget && (
                <div className="text-sm">
                  <span className="font-medium">Budget: </span>
                  {serviceRequest.budget.min && `$${serviceRequest.budget.min}`}
                  {serviceRequest.budget.min && serviceRequest.budget.max && ' - '}
                  {serviceRequest.budget.max && `$${serviceRequest.budget.max}`}
                </div>
              )}

              {serviceRequest.timeline?.deadline && (
                <div className="text-sm">
                  <span className="font-medium">Deadline: </span>
                  {new Date(serviceRequest.timeline.deadline).toLocaleDateString()}
                </div>
              )}
            </CardContent>
          </Card>

          <Separator />

          {/* Response Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Response Message *</Label>
              <Textarea
                id="message"
                placeholder="Describe your approach to this service request, your experience, and why you're the right choice..."
                value={formData.message}
                onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                className="min-h-[120px]"
                required
              />
            </div>

            {/* Cost and Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimatedCost">Estimated Cost ($)</Label>
                <Input
                  id="estimatedCost"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.estimatedCost}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, estimatedCost: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="estimatedDuration">Estimated Duration</Label>
                <Input
                  id="estimatedDuration"
                  placeholder="e.g., 2-3 hours, 1 day"
                  value={formData.estimatedDuration}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, estimatedDuration: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="availability">Availability</Label>
                <Input
                  id="availability"
                  placeholder="e.g., Next week, Weekends only"
                  value={formData.availability}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, availability: e.target.value }))
                  }
                />
              </div>
            </div>

            {/* Certifications */}
            <div className="space-y-3">
              <Label>Relevant Certifications</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add certification..."
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                />
                <Button type="button" variant="outline" onClick={addCertification}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {formData.certifications.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.certifications.map((cert, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {cert}
                      <button
                        type="button"
                        onClick={() => removeCertification(index)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Portfolio Items */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Portfolio Examples (Optional)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addPortfolioItem}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Example
                </Button>
              </div>

              {portfolioItems.map((item, index) => (
                <Card key={index} className="p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <Label className="text-sm font-medium">Example {index + 1}</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePortfolioItem(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Input
                          placeholder="Project title..."
                          value={item.title}
                          onChange={(e) => updatePortfolioItem(index, 'title', e.target.value)}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Image URL (optional)"
                          value={item.imageUrl || ''}
                          onChange={(e) => updatePortfolioItem(index, 'imageUrl', e.target.value)}
                          disabled={!!item.imageFile}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleFileUpload(index)}
                          disabled={isUploading && currentPortfolioIndex === index}
                        >
                          {isUploading && currentPortfolioIndex === index ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Show file preview if uploaded */}
                    {item.imageFile && (
                      <div className="flex items-center gap-2 p-2 bg-muted rounded">
                        <FileImage className="h-4 w-4" />
                        <span className="text-sm">{item.imageFile.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            updatePortfolioItem(index, 'imageFile', undefined);
                            updatePortfolioItem(index, 'imageUrl', '');
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    <Textarea
                      placeholder="Describe this project and its relevance..."
                      value={item.description}
                      onChange={(e) => updatePortfolioItem(index, 'description', e.target.value)}
                      className="min-h-[80px]"
                    />
                  </div>
                </Card>
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !formData.message.trim()}>
                {isSubmitting ? 'Submitting...' : 'Submit Response'}
              </Button>
            </DialogFooter>
          </form>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
